import { Popover, TextField } from "@mui/material"
import { useState, useEffect, useMemo } from 'react';
import { useSelector } from "react-redux";
import * as selector from '../../selectors/selectors';
import setToast from "../../helpers/toaster.helper";
import { ToasterTexts } from "../shared/constants";
import { ToasterType } from "../shared/toaster/toaster.model";
import { UserService } from "../../services/UserService";
import Avatar from "../shared/avatar.component";
import { UtilityService } from "../../services/UtilityService";
import { MessageService } from "../../services/MessageService";
import { ConversationCardModel, StatusEnum } from "../../models/conversations";
import { DownArrow, UpArrow } from "../../icon";
import { UserStatus } from "../../models/users.model";


const ALL_STATUS_OPTIONS = [
    { value: StatusEnum.Open, label: 'Open', color: "primary-success" },
    { value: StatusEnum.InProgress, label: 'In Progress', color: "primary-warning" },
    { value: StatusEnum.FollowUp, label: 'Follow Up', color: "circular-bar-circle-send-circle-progress" },
    { value: StatusEnum.Resolved, label: 'Resolved', color: "primary-light" },
    { value: StatusEnum.Closed, label: 'Closed', color: "filter-selected" }
]

const UNASSIGNED_USER = {
    ID: 'unassigned',
    FirstName: 'Unassigned',
    LastName: '',
};

interface AssignAndStatusProps {
    PhoneNumber: string;
    CardData: ConversationCardModel;
    ShowAssignee: boolean;
    ShowStatus: boolean;
    onStatusPopoverClose?: () => void;
}


const AssignAndStatus: React.FC<AssignAndStatusProps> = (props: AssignAndStatusProps) => {
    const Users = useSelector(selector.users);
    const IsMobileDevice = useSelector(selector.isMobileDevice)
    const IsDarkMode = useSelector(selector.isDarkMode)
    const currentUser = UserService.getCurrentUserFromLocalStorage();

    const [showAssignDropdown, setShowAssignDropDown] = useState<Element | null>(null)
    const [showStatusDropDown, setShowStatusDropDown] = useState<Element | null>(null)
    const [searchText, setSearchText] = useState<string>("");

    const modifiedUsers = useMemo(() => {
        let filterUser = Users.filter(x => x.PhoneNumberID === UserService.getUserPhoneNumberId() && x.UserStatus !== UserStatus.PENDING) ?? []

        const updatedUsers = filterUser.map(user =>
            user.ID === currentUser.ID
                ? { FirstName: 'Assign to Me', LastName: "", ID: user.ID }
                : { FirstName: user.FirstName, LastName: user.LastName, ID: user.ID }
        );
        return [...updatedUsers, UNASSIGNED_USER];
    }, [Users, currentUser.ID]);

    const [status, setStatus] = useState(ALL_STATUS_OPTIONS[0]);
    const [assignedTo, setAssignedTo] = useState(UNASSIGNED_USER);

    useEffect(() => {
        setAssignedTo(modifiedUsers.find(x => x.ID === props.CardData?.Assignee) || UNASSIGNED_USER);
        const statusValue = props.CardData?.AssignStatus ?? ALL_STATUS_OPTIONS[0].value;
        setStatus(ALL_STATUS_OPTIONS.find(x => x.value === statusValue) ?? ALL_STATUS_OPTIONS[0]);
    }, [props.PhoneNumber, props.CardData, modifiedUsers]);

    const handleChangeAssignTo = (value) => {
        if (!value) return;
        const data = {
            "contactNumber": props.PhoneNumber,
            "status": 0,
            "assignee": value.ID === "unassigned" ? null : value.ID,
            "isStatusChange": false,
            "isAssigneeChange": true
        }
        setAssignedTo(value)
        MessageService.updateConversationAssignStatus(data).then((res) => {
        }).catch((err) => {
            setToast(ToasterTexts.SOMETHING_WENT_WRONG_MESSAGE, ToasterType.DANGER);
            setAssignedTo(Users.find(x => x.ID === props.CardData?.Assignee) || UNASSIGNED_USER)
        })
        setShowAssignDropDown(null)
    };

    const handleChangeStatus = (value) => {
        if (!value) return;
        const data = {
            "contactNumber": props.PhoneNumber,
            "status": value.value,
            "assignee": assignedTo.ID,
            "isStatusChange": true,
            "isAssigneeChange": false
        }
        setStatus(value)
        MessageService.updateConversationAssignStatus(data).then((res) => {
        }).catch((err) => {
            setStatus(ALL_STATUS_OPTIONS.find(x => x.value === props.CardData?.AssignStatus))
            setToast(ToasterTexts.SOMETHING_WENT_WRONG_MESSAGE, ToasterType.DANGER);
        })
        props.onStatusPopoverClose?.()
        setShowStatusDropDown(null)
    };

    return (
        <div className={`d-flex justify-between ${IsMobileDevice ? "w-p-100" : ""}`} >
            {props.ShowStatus && <div className={`d-flex align-center c-pointer justify-between br-50 ${IsMobileDevice ? "pl-1" : "px-2"}`} onClick={(e) => { e.stopPropagation(); e.preventDefault(); setShowStatusDropDown(e.currentTarget) }} style={{ border: IsMobileDevice ? "none" : `0.0625rem solid var(--color-${status?.color})` }}>
                <div className={`w-1 h-1 br-50 bg-color-${status?.color} ticket-${status?.label.toLowerCase().replace(" ", "")}`}></div>
                <div className={`${IsMobileDevice ? "h4-regular ml-1" : "h4-semibold ml-2"}  mr-1 color-${IsDarkMode ? "primary-secondary" : status?.color}`}>{IsMobileDevice ? status.label.replace(" ", "") : status.label}</div>
                <div className="c-pointer d-flex align-center">{!Boolean(showStatusDropDown) ? <DownArrow /> : <UpArrow />}</div>
            </div>
            }
            {props.ShowAssignee &&
                <div className={`d-flex align-center ${IsMobileDevice ? "w-p-100 justify-center" : ""}`}>
                    <div className={`${IsMobileDevice ? "mb-1 w-p-85 justify-between bg-color-nav-bar-mobile b-around-text-field-border-4" : "max-w-20 justify-between border-primary-light"} d-flex align-center c-pointer br-50  px-1 ml-1`} onClick={(e) => setShowAssignDropDown(e.currentTarget)} style={{ paddingBottom: "4px", paddingTop: "4px" }}>
                        <div className="d-flex align-center">
                            <Avatar Name={assignedTo.FirstName + assignedTo.LastName} Size={20} className=" page-not-found-border bg-color-primary-light color-secondary" />
                            <div className="mx-1 h4-regular">{UtilityService.trimMessage(assignedTo.FirstName + " " + assignedTo.LastName, IsMobileDevice ? 30 : 8)}</div>
                        </div>
                        <div className="c-pointer d-flex align-center">{!Boolean(showAssignDropdown) ? <DownArrow /> : <UpArrow />}</div>
                    </div>
                </div>
            }
            {showAssignDropdown &&
                <Popover
                    open={Boolean(showAssignDropdown)}
                    onClose={() => setShowAssignDropDown(null)}
                    classes={{ paper: `br-5 ${IsDarkMode ? "bg-color-sidebar-bg" : "bg-color-primary-background"} px-2 pt-2 pb-0 d-flex justify-center align-center` }}
                    anchorEl={showAssignDropdown}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    sx={{ mt: 2 }}
                >
                    <div >
                        <TextField
                            placeholder="Search User"
                            onChange={(e) => setSearchText(e.target?.value)}
                            size="small"
                        />
                        <div className="bb-primary-divider-4 mt-1"></div>
                        <div className="max-h-23 mt-1 o-y-scroll">
                            {modifiedUsers.filter((x) =>
                                `${x.FirstName} ${x.LastName}`
                                    .toLowerCase()
                                    .includes(searchText.toLowerCase())
                            ).map((x, index) => (
                                <div className="d-flex  align-center py-1 c-pointer" onClick={() => handleChangeAssignTo(x)}>
                                    <Avatar Name={`${x.FirstName} ${x.LastName}`} Size={23} Index={index} />
                                    <div className={`ml-1 h5-regular  ${x?.ID === assignedTo?.ID ? "color-primary-light" : "color-primary-accent"}`}>{UtilityService.trimMessage(x.FirstName + " " + x.LastName, 15)}</div>
                                </div>
                            ))
                            }
                        </div>
                    </div>
                </Popover>
            }
            {showStatusDropDown &&
                <Popover
                    open={Boolean(showStatusDropDown)}
                    onClose={() => {
                        setShowStatusDropDown(null);
                        props.onStatusPopoverClose?.();
                    }}
                    classes={{ paper: `br-4 ${IsMobileDevice ? "w-16" : "w-16"} box-shadow-primary-header-shado ${IsDarkMode ? "bg-color-sidebar-bg" : "bg-color-primary-background"} p-2 d-flex justify-center align-center` }}
                    anchorEl={showStatusDropDown}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    sx={{ mt: 2 }}
                >
                    <div >
                        {ALL_STATUS_OPTIONS.map(x => (
                            <div className="d-flex  align-center py-1 c-pointer" onClick={() => handleChangeStatus(x)}>
                                <div className={` ticket-${x.label.toLowerCase().replace(" ", "")} w-2 h-2 br-50 bg-color-${x.color}`}></div>
                                <div className={`ml-2 h4-regular ${x.value === status?.value ? "color-primary-light" : `color-${IsDarkMode ? "con-hist-msg-date" : "primary-accent"}`}`}>{x.label}</div>
                            </div>
                        ))
                        }
                    </div>
                </Popover>
            }
        </div >
    )
}

export default AssignAndStatus;